// packages/web/src/services/pincode.service.ts

interface PostOffice {
  Name: string;
  Description: string | null;
  BranchType: string;
  DeliveryStatus: string;
  Circle: string;
  District: string;
  Division: string;
  Region: string;
  Block: string;
  State: string;
  Country: string;
  Pincode: string;
}

interface PincodeApiResponse {
  Message: string;
  Status: string;
  PostOffice: PostOffice[] | null;
}

export interface PincodeDetails {
  pincode: string;
  city: string;
  state: string;
  district: string;
  isValid: boolean;
  deliveryAvailable: boolean;
}

class PincodeService {
  private readonly API_BASE_URL = 'https://api.postalpincode.in';

  /**
   * Validate pincode and get location details
   */
  async validatePincode(pincode: string): Promise<PincodeDetails> {
    try {
      // Clean pincode (remove spaces, ensure 6 digits)
      const cleanPincode = pincode.trim().replace(/\s/g, '');
      
      if (!/^\d{6}$/.test(cleanPincode)) {
        return {
          pincode: cleanPincode,
          city: '',
          state: '',
          district: '',
          isValid: false,
          deliveryAvailable: false,
        };
      }

      const response = await fetch(`${this.API_BASE_URL}/pincode/${cleanPincode}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch pincode details');
      }

      const data: PincodeApiResponse[] = await response.json();
      
      // API returns array with single object
      const result = data[0];
      
      if (result.Status === 'Error' || !result.PostOffice || result.PostOffice.length === 0) {
        return {
          pincode: cleanPincode,
          city: '',
          state: '',
          district: '',
          isValid: false,
          deliveryAvailable: false,
        };
      }

      // Get first post office with delivery status
      const mainPostOffice = result.PostOffice.find(po => po.DeliveryStatus === 'Delivery') || result.PostOffice[0];
      
      return {
        pincode: cleanPincode,
        city: mainPostOffice.Block || mainPostOffice.District, // Use Block as city, fallback to District
        state: mainPostOffice.State,
        district: mainPostOffice.District,
        isValid: true,
        deliveryAvailable: mainPostOffice.DeliveryStatus === 'Delivery',
      };
    } catch (error) {
      console.error('Error validating pincode:', error);
      return {
        pincode: pincode.trim(),
        city: '',
        state: '',
        district: '',
        isValid: false,
        deliveryAvailable: false,
      };
    }
  }

  /**
   * Get all post offices for a pincode
   */
  async getPostOffices(pincode: string): Promise<PostOffice[]> {
    try {
      const cleanPincode = pincode.trim().replace(/\s/g, '');
      
      const response = await fetch(`${this.API_BASE_URL}/pincode/${cleanPincode}`);
      const data: PincodeApiResponse[] = await response.json();
      
      const result = data[0];
      
      if (result.Status === 'Error' || !result.PostOffice) {
        return [];
      }

      return result.PostOffice;
    } catch (error) {
      console.error('Error fetching post offices:', error);
      return [];
    }
  }
}

export const pincodeService = new PincodeService();
